var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { generateUniqueId, loadResource } from '../dom/index';
import { ComponentEvent, ComponentEventType } from './componentEvent';
/**
 * Base class for all components.
 */
var Component = /** @class */ (function () {
    /**
     * Constructor
     *
     * @param window The reference to the window object
     * @param options The component options
     */
    function Component(window, options) {
        if (!window)
            throw new Error('Missing "window" argument.');
        if (!options)
            throw new Error('Missing "options" argument.');
        this.isInitialized = false;
        this.isMounted = false;
        this.resourcesLoaded = false;
        this.id = '';
        this.rootElement = null;
        this.window = window;
        this.options = options;
        this.disposed = false;
    }
    /**
     * Create the root element hat will "encapsulate" the rest of the elements.
     */
    Component.prototype.createRootElement = function () {
        if (this.rootElement)
            return;
        var parent = this.getParentElement();
        this.rootElement = this.getDocument().createElement(this.getOptions().tag);
        this.id = generateUniqueId(this.getDocument(), 'ufe-');
        this.rootElement.id = this.id;
        parent.appendChild(this.rootElement);
    };
    /**
     * Get the parent element that hosts this component.
     */
    Component.prototype.getParentElement = function () {
        var parent = null;
        var opt = this.getOptions();
        if (opt.parent) {
            if (typeof opt.parent === 'string') {
                parent = this.getDocument().querySelector(opt.parent);
            }
            else {
                parent = opt.parent;
            }
        }
        if (!parent)
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error("Failed to find parent \"".concat(opt.parent, "\"."));
        return parent;
    };
    /**
     * Load the resources required by the component.
     */
    Component.prototype.loadResources = function () {
        return __awaiter(this, void 0, void 0, function () {
            var options, document_1, _i, _a, resource;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (this.resourcesLoaded)
                            return [2 /*return*/];
                        this.resourcesLoaded = true;
                        options = this.getOptions();
                        if (!(options.resources && options.resources.length > 0)) return [3 /*break*/, 4];
                        document_1 = this.getDocument();
                        _i = 0, _a = options.resources;
                        _b.label = 1;
                    case 1:
                        if (!(_i < _a.length)) return [3 /*break*/, 4];
                        resource = _a[_i];
                        // DO NOT LOAD ALL AT ONCE AS YOU MIGHT HAVE DEPENDENCIES
                        // AND A RESOURCE MIGHT LOAD BEFORE IT'S DEPENDENCY
                        return [4 /*yield*/, loadResource(document_1, resource.url, resource.isScript, resource.skip, resource.attributes)];
                    case 2:
                        // DO NOT LOAD ALL AT ONCE AS YOU MIGHT HAVE DEPENDENCIES
                        // AND A RESOURCE MIGHT LOAD BEFORE IT'S DEPENDENCY
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        _i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get the options data.
     */
    Component.prototype.getOptions = function () {
        return (this.options);
    };
    /**
     * Get the window reference.
     */
    Component.prototype.getWindow = function () { return this.window; };
    /**
     * Get the document reference.
     */
    Component.prototype.getDocument = function () { return this.getWindow().document; };
    /**
     * Core initialization function.
     * Any component derived should override this to add extra functionality.
     */
    Component.prototype.initializeCore = function () { return Promise.resolve(); };
    /**
     * Core mount function.
     * Any component derived should override this to add extra functionality.
     */
    Component.prototype.mountCore = function () {
        // This needs to be handled by each component
        // this.callHandler(ComponentEventType.Mounted);
        return Promise.resolve();
    };
    /**
     * Core dispose function.
     * Any component derived should override this to add clean-up after itself.
     */
    Component.prototype.disposeCore = function () { return Promise.resolve(); };
    /**
     * Call the global error handler.
     *
     * @param e The error object
     */
    Component.prototype.callErrorHandler = function (e) {
        var _a;
        var handler = (_a = this.options.handlers) === null || _a === void 0 ? void 0 : _a.error;
        if (handler) {
            try {
                handler(new ComponentEvent(this.id, ComponentEventType.Error, this.rootElement, this.getParentElement(), e));
            }
            catch (error) {
                this.log(error);
            }
        }
        else {
            this.log(e);
        }
    };
    /**
     * Call a specific event handler.
     *
     * @param type The type of handler to call.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    Component.prototype.callHandler = function (type, data) {
        if (type === ComponentEventType.Error)
            throw new Error("For calling the \"".concat(ComponentEventType.Error, "\" handler use the \"callErrorHandler\" method."));
        var handler = this.options.handlers
            ? this.options.handlers[type]
            : null;
        if (handler) {
            try {
                var event_1 = new ComponentEvent(this.id, type, this.rootElement, this.getParentElement(), null);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                event_1.data = data;
                handler(event_1);
            }
            catch (error) {
                this.callErrorHandler(error);
            }
        }
    };
    /**
     * Logging method.
     *
     * @param message The message.
     * @param optionalParams Optional parameters.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    Component.prototype.log = function (message) {
        var _a, _b;
        var optionalParams = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            optionalParams[_i - 1] = arguments[_i];
        }
        /* eslint-disable @typescript-eslint/no-unsafe-assignment */
        /* eslint-disable @typescript-eslint/no-unsafe-member-access */
        var logMethod = (_b = (_a = this.window) === null || _a === void 0 ? void 0 : _a.console) === null || _b === void 0 ? void 0 : _b.log;
        if (logMethod)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            logMethod(message, optionalParams);
        /* eslint-enable @typescript-eslint/no-unsafe-member-access */
        /* eslint-enable @typescript-eslint/no-unsafe-assignment */
    };
    /**
     * Method invoked to initialize the component.
     * It should create the root element and any base dependencies.
     */
    Component.prototype.initialize = function () {
        return __awaiter(this, void 0, void 0, function () {
            var e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isInitialized)
                            return [2 /*return*/, this];
                        this.callHandler(ComponentEventType.BeforeCreate);
                        this.isInitialized = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, this.loadResources()];
                    case 2:
                        _a.sent();
                        this.createRootElement();
                        return [4 /*yield*/, this.initializeCore()];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_1 = _a.sent();
                        this.callErrorHandler(e_1);
                        return [3 /*break*/, 5];
                    case 5:
                        this.callHandler(ComponentEventType.Created);
                        return [2 /*return*/, this];
                }
            });
        });
    };
    /**
     * Method invoked to mount the actual content of the component.
     */
    Component.prototype.mount = function () {
        return __awaiter(this, void 0, void 0, function () {
            var e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.isInitialized) {
                            this.callErrorHandler(new Error('Call "initialize" before calling "mount".'));
                            return [2 /*return*/, this];
                        }
                        if (this.isMounted)
                            return [2 /*return*/, this];
                        this.callHandler(ComponentEventType.BeforeMount);
                        this.isMounted = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.mountCore()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_2 = _a.sent();
                        this.callErrorHandler(e_2);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/, this];
                }
            });
        });
    };
    /**
     * Method invoked to dispose of the component.
     */
    Component.prototype.dispose = function () {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var e_3;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (this.disposed)
                            return [2 /*return*/];
                        this.callHandler(ComponentEventType.BeforeDestroy);
                        this.disposed = true;
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.disposeCore()];
                    case 2:
                        _c.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_3 = _c.sent();
                        this.callErrorHandler(e_3);
                        return [3 /*break*/, 4];
                    case 4:
                        this.callHandler(ComponentEventType.Destroyed);
                        this.id = '';
                        this.isInitialized = false;
                        this.isMounted = false;
                        this.resourcesLoaded = false;
                        (_b = (_a = this.rootElement) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.removeChild(this.rootElement);
                        this.rootElement = null;
                        this.window = null;
                        return [2 /*return*/];
                }
            });
        });
    };
    return Component;
}());
export { Component };
//# sourceMappingURL=component.js.map